import React, { FC, useContext, useEffect, useState } from 'react'
import { Button, Drawer, Form, Input, Popconfirm, Space, Table } from 'antd'
import { useTranslation } from 'react-i18next'
import Section from '../../components/Layout/Section'
import MainButton from '../../components/MainButton/MainButton'
import ApiContext from '../../context/ApiContext'
import { SectorModel } from '../../domain/sector.model'
import { SectorLabelsReq, SectorReq } from '../../services/Api'
import styles from '../Events/DemandesContact.module.scss'
import useBreakpointValue from '../../utils/useBreakpointValue.hook'

const Sectors: FC = () => {
  const { t } = useTranslation('sector')
  const { t: t_common } = useTranslation('common')

  const { getSectors, getSectorLocales, createSector, updateSector, deleteSector, successFormBehaviour, errorFormBehaviour } = useContext(ApiContext)

  const [sectors, setSectors] = useState<SectorModel[]>([])
  const [filteredSectors, setFilteredSectors] = useState<SectorModel[]>([])
  const [supportedLocales, setSupportedLocales] = useState<string[]>(['en', 'fr'])
  const [createVisible, setCreateVisible] = useState(false)
  const [selectedSector, setSelectedSector] = useState<SectorModel | undefined>()

  const drawerWidth = useBreakpointValue<string>({ xs: '100%', md: '80%', lg: '65%', xl: '50%' })

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const reload = () => {
    getSectors().then((res: SectorModel[]) => {
      setSectors(res)
      setFilteredSectors(res)
    })
  }

  useEffect(() => {
    getSectorLocales().then(setSupportedLocales)
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filterSectors = (search: string) => {
    const s = search.trim().toLowerCase()
    if (!s) {
      setFilteredSectors(sectors)
    } else {
      setFilteredSectors(
        sectors.filter((sector) =>
          [sector.id, ...Object.values(sector.labels)].join(' ').toLowerCase().includes(s),
        ),
      )
    }
  }

  const handleCreate = () => {
    createForm.validateFields().then((values) => {
      const req: SectorReq = {
        id: values.id,
        labels: supportedLocales.reduce((acc, locale) => {
          if (values[`label_${locale}`]) acc[locale] = values[`label_${locale}`]
          return acc
        }, {} as Record<string, string>),
      }
      createSector(req)
        .then(() => successFormBehaviour(t('sectorCreated')))
        .then(() => { createForm.resetFields(); setCreateVisible(false); reload() })
        .catch(errorFormBehaviour)
    })
  }

  const handleEdit = () => {
    editForm.validateFields().then((values) => {
      if (!selectedSector) return
      const req: SectorLabelsReq = {
        labels: supportedLocales.reduce((acc, locale) => {
          if (values[`label_${locale}`]) acc[locale] = values[`label_${locale}`]
          return acc
        }, {} as Record<string, string>),
      }
      updateSector(selectedSector.id, req)
        .then(() => successFormBehaviour(t('sectorSaved')))
        .then(() => { editForm.resetFields(); setSelectedSector(undefined); reload() })
        .catch(errorFormBehaviour)
    })
  }

  const handleDelete = (id: string) => {
    deleteSector(id)
      .then(() => successFormBehaviour(t('sectorDeleted')))
      .then(reload)
      .catch((err: any) => {
        if (err.response?.status === 409) {
          errorFormBehaviour({ response: { status: 409, data: { message: t('cannotDelete') } } })
        } else {
          errorFormBehaviour(err)
        }
      })
  }

  const columns = [
    {
      title: t('code'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: SectorModel, b: SectorModel) => a.id.localeCompare(b.id),
    },
    ...supportedLocales.map((locale) => ({
      title: t(`label_${locale}` as any, locale.toUpperCase()),
      key: `label_${locale}`,
      render: (_: any, record: SectorModel) => record.labels?.[locale] ?? '',
    })),
    {
      key: 'actions',
      render: (_: any, record: SectorModel) => (
        <Space>
          <Button size="small" onClick={() => {
            setSelectedSector(record)
            editForm.setFieldsValue(
              supportedLocales.reduce((acc, locale) => {
                acc[`label_${locale}`] = record.labels?.[locale] ?? ''
                return acc
              }, {} as Record<string, string>),
            )
          }}>
            {t_common('see')}
          </Button>
          <Popconfirm
            title={t('confirmDeletion')}
            onConfirm={() => handleDelete(record.id)}
            okText={t_common('yes')}
            cancelText={t_common('no')}
          >
            <Button size="small" danger>{t('deleteSector')}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Section>
        <div className={styles.TitleBloc}>
          <h2>{t('listOfSectors')}</h2>
          <MainButton onClick={() => setCreateVisible(true)}>{t('addSector')}</MainButton>
        </div>
        <div className={'search-bar-container'}>
          <label className={'search-label'}>{t('search_among_sectors')}</label>
          <Input.Search
            placeholder="ex: FINTECH"
            allowClear
            onChange={(e) => filterSectors(e.target.value)}
            style={{ width: 300 }}
          />
        </div>
        <br />
        <Table
          rowKey={(s: SectorModel) => s.id}
          columns={columns}
          dataSource={filteredSectors}
        />
      </Section>

      {/* Create drawer */}
      <Drawer
        title={t('addNewSector')}
        width={drawerWidth}
        onClose={() => { createForm.resetFields(); setCreateVisible(false) }}
        open={createVisible}
        footer={
          <Space>
            <MainButton onClick={handleCreate}>{t_common('add')}</MainButton>
            <Button onClick={() => { createForm.resetFields(); setCreateVisible(false) }}>{t_common('cancel')}</Button>
          </Space>
        }
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            label={t('code')}
            name="id"
            rules={[
              { required: true, message: t_common('requiredField') },
              { pattern: /^[A-Z][A-Z0-9_]*$/, message: t('idFormat') },
            ]}
          >
            <Input placeholder="e.g. HEALTH_TECH" />
          </Form.Item>
          {supportedLocales.map((locale) => (
            <Form.Item
              key={locale}
              label={`Label (${locale.toUpperCase()})`}
              name={`label_${locale}`}
              rules={locale === 'en' ? [{ required: true, message: t_common('requiredField') }] : []}
            >
              <Input />
            </Form.Item>
          ))}
        </Form>
      </Drawer>

      {/* Edit drawer */}
      <Drawer
        title={t('modifySector')}
        width={drawerWidth}
        onClose={() => { editForm.resetFields(); setSelectedSector(undefined) }}
        open={!!selectedSector}
        footer={
          <Space>
            <MainButton onClick={handleEdit}>{t_common('save')}</MainButton>
            <Button onClick={() => { editForm.resetFields(); setSelectedSector(undefined) }}>{t_common('cancel')}</Button>
          </Space>
        }
      >
        <Form form={editForm} layout="vertical">
          {supportedLocales.map((locale) => (
            <Form.Item
              key={locale}
              label={`Label (${locale.toUpperCase()})`}
              name={`label_${locale}`}
              rules={locale === 'en' ? [{ required: true, message: t_common('requiredField') }] : []}
            >
              <Input />
            </Form.Item>
          ))}
        </Form>
      </Drawer>
    </>
  )
}

export default Sectors
